import React from "react";

type ComputeHardwareSummaryTone = "highlight" | "muted";
type ComputeHardwareSummaryAlign = "center" | "right";

const hardwareSummaryGradientAnimationClassName =
  "[background-position:0%_50%] [background-size:200%_100%] transition-[background-position,filter] duration-500 ease-out motion-reduce:transition-none group-hover:[background-position:100%_50%]";

const defaultHardwareSummaryGradientClassName =
  "bg-[linear-gradient(90deg,#fff_0%,#e4e4e4_25%,#a9a9ab_50%,#e4e4e4_75%,#fff_100%)]";

const toneClassNames: Record<
  ComputeHardwareSummaryTone,
  {
    count: string;
    detail: string;
    detailSize: string;
    name: string;
    nameSize: string;
  }
> = {
  highlight: {
    count: "",
    detail: "",
    detailSize: "text-sm",
    name: "",
    nameSize: "text-lg",
  },
  muted: {
    count: "text-zinc-500",
    detail: "text-zinc-500",
    detailSize: "text-xs",
    name: "text-zinc-500",
    nameSize: "text-sm",
  },
};

const alignmentClassNames: Record<
  ComputeHardwareSummaryAlign,
  {
    container: string;
    detail: string;
    row: string;
  }
> = {
  center: {
    container: "mx-auto items-center text-center",
    detail: "justify-center text-center",
    row: "justify-center text-center",
  },
  right: {
    container: "ml-auto items-end text-right",
    detail: "justify-end text-right",
    row: "justify-end text-right",
  },
};

export const ComputeHardwareSummary: React.FC<{
  align?: ComputeHardwareSummaryAlign;
  count?: number;
  detail?: React.ReactNode;
  gradientClassName?: string;
  name: React.ReactNode;
  tone?: ComputeHardwareSummaryTone;
}> = ({
  align = "right",
  count,
  detail,
  gradientClassName,
  name,
  tone = "highlight",
}) => {
  const toneClasses = toneClassNames[tone];
  const alignmentClasses = alignmentClassNames[align];
  const hardwareTextGradientClassName =
    tone === "highlight"
      ? `${gradientClassName || defaultHardwareSummaryGradientClassName} ${hardwareSummaryGradientAnimationClassName} bg-clip-text text-transparent drop-shadow-sm`
      : "";

  return (
    <div
      className={`flex h-full w-fit max-w-full flex-col justify-center overflow-hidden px-4 py-2 ${alignmentClasses.container}`}
    >
      <div
        className={`w-fit max-w-full min-w-0 ${hardwareTextGradientClassName}`}
      >
        <div
          className={`inline-block w-fit max-w-40 min-w-0 [text-wrap:balance] text-sm font-bold leading-[1.1] ${alignmentClasses.row}`}
        >
          {typeof count === "number" ? (
            <span
              className={`shrink-0 text-sm font-black uppercase tracking-[.06em] mr-1 ${toneClasses.count}`}
            >
              {count} x
            </span>
          ) : null}
          <span
            className={`font-black leading-[1.1] tracking-[.02em] ${toneClasses.nameSize} ${toneClasses.name}`}
          >
            {name}
          </span>
        </div>
        {detail ? (
          <p
            className={`mt-0.5 flex w-full min-w-0 items-center font-semibold leading-[1.1] ${alignmentClasses.detail} ${toneClasses.detailSize} ${toneClasses.detail}`}
          >
            {detail}
          </p>
        ) : null}
      </div>
    </div>
  );
};

export const ComputeHardwareInlineSummary: React.FC<{
  children: React.ReactNode;
  gradientClassName?: string;
  tone?: ComputeHardwareSummaryTone;
}> = ({ children, gradientClassName, tone = "highlight" }) => (
  <span
    className={`inline-flex min-w-0 truncate text-sm font-black leading-5 tracking-[.02em] ${
      tone === "highlight"
        ? `${gradientClassName || defaultHardwareSummaryGradientClassName} ${hardwareSummaryGradientAnimationClassName} bg-clip-text !text-transparent drop-shadow-sm`
        : "!text-zinc-500"
    }`}
  >
    {children}
  </span>
);
